import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogService } from '@serviceplan/ui-kit';
import { DownloadAssetsService } from '@thehubz/client/asset-services';
import {
  AppSettings,
  AssetsGenerateUrlService,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  ConfigObjectModel,
  ENVIRONMENT,
  ResizeService,
  ValueListsService,
  ViewType,
} from '@thehubz/client/core';
import { Asset, AssetDetails, AssetsApiService, Collection, CollectionResultResponse, CollectionsApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { Observable, Subscription, lastValueFrom, merge, of, timer } from 'rxjs';
import { debounce, delay, filter, skip, tap } from 'rxjs/operators';
import { AssetViewTypeService, GridConfig } from '../../../services/asset-view-type.service';
import { CollectionEditStepperService } from '../../../services/collection-edit-stepper.service';
import { CollectionPublishingStepperService } from '../../../services/collection-publishing-stepper.service';
import { SharingStepperDialogService } from '../../../services/sharing-stepper-dialog.service';
import { AssetDownloadComponent, AssetDownloadData } from '../../asset-download/asset-download.component';
import { CollectionInfo } from '../collection-info.model';
import { CollectionAssetPreviewService } from '../services/collection-asset-preview.service';
import { CollectionEventsService } from '../services/collection-events.service';
import { CollectionSelectionService } from '../services/collection-selection.service';

@Component({
  selector: 'thehubz-collections-list',
  templateUrl: './collections-list.component.html',
  styleUrls: ['./collections-list.component.scss'],
})
export class CollectionsListComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() assetDetails = '';
  @Input() configObject!: ConfigObjectModel;
  @Input() sharedCollection!: CollectionInfo;
  @Input() gridSettingsKeySuffix = 'CollectionsOverview';
  @Input() basketDataLoaded = false;
  @Input() collectionList: CollectionInfo[] = [];
  @Input() showFilterBubbles = true;
  @Input() viewType: Observable<ViewType> = of();
  /**
   * This 2d array is created for use with the cdkVirtualScroll
   */
  @Input() collectionSplitList?: CollectionInfo[][];
  @Input() selectedViewSection2: ViewType = 'sortByLastCreated';
  @Input() sortByNew = true;
  @Output() sortPublicCollections = new EventEmitter<AnyType>();
  @Output() publicCollectionChanged = new EventEmitter<{ action: string; data: AnyType }>();
  @Output() updatePublishedCollection = new EventEmitter();
  @Output() collectionListToEmit = new EventEmitter<CollectionResultResponse>();

  defaultSort: { [key: string]: number } = { createdAt: -1 };
  sortData: { [key: string]: number } = {};

  filterObject = {
    filters: {
      assetId: '',
      search: '',
    },
    sort: this.defaultSort,
  };
  size = 4;
  currentSize: AnyType;
  prefix = 'is-';
  sizes = AppSettings.SCREEN_SIZES;
  appliedFilters = 0;
  collectionCount = 0;
  assetAspectRatio: AssetDetails[] = [];
  brandingKey: CustomClients;
  selectedViewType!: ViewType;
  selectedFilterObject: AnyType;
  filterBubbles: { _id: string; name: string }[] = [];

  /**
   * True when data is loading from server
   */
  httpCallInProgress = false;

  gridConfig: GridConfig = {
    showListView: false,
    showGridView: true,
    showGridViewSmall: true,
    showGridViewLarge: false,
    showGridViewSidebar: false,
    gridSize: 4,
  };
  private resizeSubscription?: Subscription;
  private collectionListSearchFilter?: Subscription;
  private collectionSub?: Subscription;
  private collectionListChangeViewSub?: Subscription;
  private collectionListSortBySub?: Subscription;
  private collectionsSearchFilterBubblesSub?: Subscription;

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    private dialog: MatDialog,
    private elementRef: ElementRef,
    private resizeSvc: ResizeService,
    private assetApiService: AssetsApiService,
    private confirmDialog: ConfirmDialogService,
    private commonUtilityLib: CommonUtilityLib,
    private collectionAPI: CollectionsApiService,
    private valueListsService: ValueListsService,
    private assetUrlService: AssetsGenerateUrlService,
    private downloadAssetsService: DownloadAssetsService,
    protected assetViewTypeService: AssetViewTypeService,
    private collectionEventsService: CollectionEventsService,
    private collectionSelectionService: CollectionSelectionService,
    private sharingStepperDialogService: SharingStepperDialogService,
    private collectionEditStepperService: CollectionEditStepperService,
    private collectionAssetPreviewService: CollectionAssetPreviewService,
    private collectionPublishingStepperService: CollectionPublishingStepperService,
    private readonly clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {
    this.brandingKey = this.env.brandingKey;
    this.resizeSubscription = this.resizeSvc.onResize$.pipe(delay(0)).subscribe(() => {
      const currentSize = this.sizes.find((x) => {
        const el = this.elementRef.nativeElement.querySelector(`.${this.prefix}${x.id}`);
        const isVisible = window.getComputedStyle(el).display != 'none';
        return isVisible;
      });
      this.currentSize = currentSize;
    });
  }

  @HostListener('window:resize', [])
  private onResize() {
    this.detectScreenSize();
  }

  ngAfterViewInit(): void {
    this.detectScreenSize();
  }

  ngOnInit(): void {
    // resigning empty string because search value assigned at parent component (collection.component.ts)
    this.collectionEventsService.collectionListSearchFilter$.next('');
    if (!this.sharedCollection && this.configObject?.callFrom !== 'shareCollection') {
      this._showLoader();
      // subscribe to the search filter
      if (this.configObject?.callFrom !== 'publishCollection') {
        this.fetchCollections();
        this.collectionListSearchFilter = this.collectionEventsService.collectionListSearchFilter$
          .pipe(
            skip(1),
            debounce(() => timer(500)),
          )
          .subscribe((data) => {
            if (data) {
              this.fetchCollections(data);
            } else {
              this.fetchCollections();
            }
          });
      } else {
        this._showLoader(false);
        this.collectionCount = this.collectionList.length;
        this.commonUtilityLib.emitNewHeaderline($localize`:headline:Public Collections:` + ' ' + this.collectionCount);
      }
    }
    /**
     * Header should not be updated if the request is coming from asset details page 'Relation tab'.
     */
    if (!this.assetDetails) {
      if (this.configObject?.callFrom !== 'publishCollection') {
        this.commonUtilityLib.emitNewHeaderline($localize`:headline:My Collections`);
      }
    }
    this.collectionListChangeViewSub = merge(
      this.viewType.pipe(filter((value) => !!value)),
      this.collectionEventsService.collectionListChangeView.pipe(filter((value) => !!value)),
    ).subscribe((value) => this.changeView(value));

    this.collectionListSortBySub = this.collectionEventsService.collectionListSortBy.subscribe((data) => {
      if (data) {
        if (this.configObject?.callFrom === 'publishCollection') {
          this.sortPublicCollections.emit(data);
        } else {
          this.sortBy(data);
        }
      }
    });

    this.collectionsSearchFilterBubblesSub = this.collectionEventsService.collectionsSearchFilterBubbles$.subscribe((data) => {
      if (data.length) {
        if (this.configObject?.callFrom === 'publishCollection') {
          this.publicCollectionChanged.emit({ action: 'showBubbles', data: data });
        } else {
          this.fetchCollections(data, true);
        }
      } else {
        this.selectedFilterObject = {};
        this.filterBubbles = this.searchFilterBubblesObj();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['sharedCollection']?.currentValue) {
      this.shareCollection = changes['sharedCollection'].currentValue;
      this.shareCollectionResult();
    }
  }

  async shareCollectionResult() {
    if (this.sharedCollection && this.configObject?.callFrom === 'shareCollection') {
      this.collectionList = [this.sharedCollection];
      this.collectionList = await this.collectionAssetPreviewService.generateCoverPreview(this.collectionList);
      this.initAssetView();
      this.assetViewTypeService.getViewType(this.gridSettingsKeySuffix);
      this.collectionSplitList = this.split(this.collectionList, this.size);
      this.basketDataLoaded = true;
    }
  }

  /**
   * get all collection list of logged in user
   * @param searchText
   */
  async fetchCollections(searchText?: string, addBubbles?: boolean) {
    if (this.assetDetails) {
      this.filterObject.filters['assetId'] = this.assetDetails;
    }
    this.filterObject['filters']['search'] = searchText || '';
    this.collectionAPI
      .collectionsControllerFindAll(JSON.stringify(this.filterObject))
      .pipe(tap(() => this._showLoader(false)))
      .subscribe({
        next: async (response) => {
          this.collectionListToEmit.emit(response);
          if (response.result.length) {
            this.collectionCount = response.result.length;
            this.collectionList = await this.collectionAssetPreviewService.generateCoverPreview(response.result);
            this.basketDataLoaded = true;
            if (addBubbles) {
              this.selectedFilterObject = { ...this.selectedFilterObject, searchText: { _id: searchText, name: searchText } };
              this.filterBubbles = this.searchFilterBubblesObj();
            }
          } else {
            this.collectionList = [];
            this.basketDataLoaded = true;
          }
          if (this.collectionCount && !this.assetDetails) {
            this.commonUtilityLib.emitNewHeaderline($localize`:headline:My Collections:` + ' ' + this.collectionCount);
          }
          this.initAssetView();
          this.selectedViewType = this.assetViewTypeService.getViewType(this.gridSettingsKeySuffix);
          this.collectionSplitList = this.split(this.collectionList, this.size);
        },
      });
  }

  /**
   * Event emitter handler from thehubz-collection-card-view component
   * @param event
   */
  collectionActions(event: { action: string; id: string }) {
    switch (event.action) {
      case 'editCollection':
        this.editCollection(event.id);
        break;
      case 'shareCollection':
        this.shareCollection(event.id);
        break;
      case 'deleteCollection':
        this.deleteCollection(event.id);
        break;
      case 'downloadCollection':
        this.downloadCollection(event.id);
        break;
      case 'publishCollection':
        this.publishCollection(event.id);
        break;
      case 'unpublishCollection':
        this.unpublishCollection(event.id);
        break;
    }
  }

  /**
   * Delete the collection
   * @param id
   */
  private deleteCollection(id?: string) {
    this.confirmDialog
      .open({
        title: $localize`Delete confirmation`,
        message: $localize`Are you sure you want to delete this collection?`,
      })
      .subscribe((confirm) => {
        if (confirm && id) {
          const collectionName = this.collectionList.find((col) => col._id === id)?.name;
          this.collectionAPI.collectionsControllerRemove(id.toString()).subscribe(() => {
            this.clientNotificationHandlerService.successNotification($localize`Collection "${collectionName}" has been deleted`);
            if (this.configObject?.callFrom === 'publishCollection') this.updatePublishedCollection.emit();
            else this.fetchCollections();
          });
        }
      });
  }

  /**
   * Share collection from the collection list view
   * @param id
   */
  private shareCollection(id: string) {
    this.assetAspectRatio = [];
    this.collectionAPI.collectionsControllerFindOne(id).subscribe((collection: Collection) => {
      const responseData = this.assetUrlService.generatePreview(collection.assets as unknown as Asset[]);
      if (responseData && responseData.length > 0) {
        responseData.forEach((asset) => {
          this.assetAspectRatio.push({
            assetId: asset._id,
            name: asset.name,
            version: asset.currentVersion?.version || 0,
            aspectRatios: asset.currentVersion?.aspectRatios || [],
          });
        });
        const stepLabel = $localize`Share collection:` + ` <span class="font-light">${collection.name}<span>`;
        this.sharingStepperDialogService.collectionSharingStepper(this.assetAspectRatio, collection, id, stepLabel);
      }
    });
  }

  private downloadCollection(id: string) {
    this.collectionAPI.collectionsControllerCollectionAssets(id).subscribe((res) => {
      this.downloadAssetsService.createDownloadData(res.assets).then((downloadData) => {
        const downloadType = $localize`Collection Download`;
        this.dialog.open<AssetDownloadComponent, AssetDownloadData>(AssetDownloadComponent, {
          width: '100%',
          maxWidth: '70rem',
          height: '100%',
          maxHeight: '50rem',
          disableClose: true,
          data: { data: downloadData, downloadType, collectionId: id },
        });
      });
    });
  }

  /**
   * Open stepper dialog for collection publishing
   */
  editCollection(id: string) {
    this.collectionAPI.collectionsControllerFindOne(id).subscribe(async (resp: Collection) => {
      this.collectionEditStepperService.collectionEditStepper(resp).then((result) => {
        if (result) {
          this.updateCollection(result);
        }
      });
    });
  }

  async publishCollection(id: string) {
    this.collectionAPI.collectionsControllerFindOne(id).subscribe(async (resp: Collection) => {
      if ([CustomClients.Arval, CustomClients.VKB, CustomClients.SEAT].includes(this.brandingKey)) {
        const listUserAgencies = await lastValueFrom(this.valueListsService.listUserAgencies());
        this.confirmDialog
          .open({
            title: $localize`Confirmation`,
            message: $localize`Are you sure you want to publish this collection with all users of the system?`,
          })
          .subscribe((confirm) => {
            if (confirm && id) {
              const published = this.collectionSelectionService.getPublishCollectionDto(listUserAgencies);
              this.collectionAPI.collectionsControllerPublish(id, { published }).subscribe({
                next: (response) => {
                  if (response) {
                    this.clientNotificationHandlerService.successNotification($localize`Collection "${resp.name}" has been published `);
                    this.collectionList.forEach((collection) => {
                      if (collection._id == response._id) collection = response;
                    });
                  }
                },
                error: (err) => {
                  this.clientNotificationHandlerService.errorNotification(err);
                },
              });
            }
          });
      } else {
        this.collectionSub = (await this.collectionPublishingStepperService.collectionPublishingStepper(resp)).subscribe((data) =>
          this.collectionList.forEach((collection) => {
            if (collection._id == data._id) collection = data;
          }),
        );
      }
    });
  }

  unpublishCollection(id: string) {
    this.confirmDialog
      .open({
        title: $localize`Unpublish collection`,
        message: $localize`Are you sure you want to unpublish this collection?`,
      })
      .subscribe((confirm) => {
        if (confirm && id) {
          this.collectionAPI.collectionsControllerUpdate(id, { published: [] }).subscribe({
            next: async (result) => {
              if (result) {
                this.collectionList.forEach((collection: Collection) => {
                  if (collection._id === result._id) {
                    collection.published = result.published;
                  }
                });

                if (this.collectionSplitList) {
                  const splitListIndex = this.collectionSplitList?.findIndex((subArr) => subArr.some((obj) => obj._id === result._id));
                  this.collectionSplitList[splitListIndex].forEach((collection: Collection) => {
                    if (collection._id === result._id) {
                      collection.published = result.published;
                    }
                  });
                }
                if (this.configObject?.callFrom === 'publishCollection') {
                  this.publicCollectionChanged.emit({ action: 'unpublished', data: result._id });
                }

                this.clientNotificationHandlerService.successNotification($localize`Collection "${result.name}" has been unpublished`);
              }
            },
            error: (err) => {
              this.clientNotificationHandlerService.errorNotification(err);
            },
          });
        }
      });
  }

  async updateCollection(updated: AnyType) {
    if (this.configObject?.callFrom === 'publishCollection') {
      this.publicCollectionChanged.emit({ action: 'updated', data: updated });
    } else {
      const collectionIndex = this.collectionList.findIndex((collection: Collection) => collection._id === updated._id);
      this.collectionList[collectionIndex] = updated;
      this.collectionList[collectionIndex].coverAssetID = await lastValueFrom(
        this.assetApiService.assetsControllerFindOne(this.collectionList[collectionIndex].coverAssetID),
      );
      this.collectionList = await this.collectionAssetPreviewService.generateCoverPreview(this.collectionList);
      this.collectionSplitList = this.split(this.collectionList, this.size);
    }
  }

  selectCollection(collection: Collection) {
    this.collectionSelectionService.updateSelectedItems('collections', [collection], false);
    this.collectionList.forEach((item) => {
      if (item._id === collection._id) item.checked = true;
    });
  }

  sortBy(event: AnyType) {
    this.sortByNew = !this.sortByNew;
    this.selectedViewSection2 = event || 'sortByLastCreated';
    if (!this.sortByNew) {
      this.sortData = { name: 1 };
    } else {
      this.sortData = this.defaultSort;
    }
    this.filterObject['sort'] = this.sortData;
    this.fetchCollections();
  }

  selectAll(check: boolean) {
    if (check) {
      this.collectionList.forEach((collection) => {
        collection.checked = true;
      });
      this.collectionSelectionService.updateSelectedItems('collections', this.collectionList, true);
    } else {
      this.collectionList.forEach((collection) => {
        collection.checked = false;
      });
      this.collectionSelectionService.updateSelectedItems('collections', this.collectionList, false);
    }
  }

  initAssetView() {
    const gridSettings = this.assetViewTypeService.getGridConfig(this.gridSettingsKeySuffix);
    this.size = gridSettings.gridSize || 4;
    if (Object.keys(gridSettings).length) {
      this.gridConfig = gridSettings;
      return;
    }
    this.assetViewTypeService.setGridConfig(this.gridSettingsKeySuffix, this.gridConfig);
  }

  changeView(view: AnyType) {
    if (view !== 'list') {
      this.gridConfig.showListView = false;
      this.gridConfig.showGridView = true;

      if (view === 'grid_4') {
        this.gridConfig.showGridViewSmall = true;
        this.gridConfig.showGridViewLarge = false;
        this.gridConfig.showGridViewSidebar = false;
        this.gridConfig.gridSize = 4;
        this.selectedViewType = 'grid_4';
        this.size = 4;
        this.collectionSplitList = this.split(this.collectionList, this.size);
      }
      if (view === 'grid_8') {
        this.gridConfig.showGridViewSmall = false;
        this.gridConfig.showGridViewLarge = true;
        this.gridConfig.showGridViewSidebar = false;
        this.gridConfig.gridSize = 8;
        this.selectedViewType = 'grid_8';
        this.size = 8;
        this.collectionSplitList = this.split(this.collectionList, this.size);
      }
      this.currentSize = this.size;
    } else {
      this.gridConfig.showGridView = false;
      this.gridConfig.showGridViewSmall = false;
      this.gridConfig.showGridViewLarge = false;
      this.gridConfig.showListView = true;
      this.selectedViewType = 'list';
    }

    this.assetViewTypeService.setGridConfig(this.gridSettingsKeySuffix, this.gridConfig);
    this.collectionEventsService.collectionListChangeView.next('');
  }

  private split(input: Collection[], groupSize: number) {
    const out = [];
    for (let i = 0; i < input.length; i += groupSize) {
      out.push(input.slice(i, i + groupSize));
    }
    return out;
  }

  private detectScreenSize() {
    const currentSize = this.sizes.find((x) => {
      const el = this.elementRef.nativeElement.querySelector(`.${this.prefix}${x.id}`);
      const isVisible = window.getComputedStyle(el).display != 'none';
      return isVisible;
    });
    this.resizeSvc.onResize(currentSize?.id);
  }

  /**
   * Create a array of object for filter bubbles
   * @returns
   */
  private searchFilterBubblesObj(): { _id: string; name: string; key: string }[] {
    if (!this.selectedFilterObject || this.commonUtilityLib.isEmptyObject(this.selectedFilterObject)) return [];
    return Object.keys(this.selectedFilterObject)
      .map((key) => {
        return {
          _id: this.selectedFilterObject[key].name,
          name: this.selectedFilterObject[key].name,
          key,
        };
      })
      .flat(1);
  }

  removeFilterBubble(filterBubble: { _id: string; name: string; key: string }) {
    const filterBubblesIndex = this.filterBubbles.findIndex((filter) => filter._id === filterBubble._id);
    if (filterBubblesIndex !== -1) {
      this.filterBubbles.splice(filterBubblesIndex, 1);
      delete this.selectedFilterObject[filterBubble.key];
      this.collectionEventsService.collectionsClearSearch.next(true);
      this.collectionEventsService.collectionsSearchFilterBubbles$.next('');
    }
  }

  removeAllFilterBubble() {
    this.selectedFilterObject = {};
    this.filterBubbles = this.searchFilterBubblesObj();
    this.collectionEventsService.collectionsClearSearch.next(true);
    this.collectionEventsService.collectionsSearchFilterBubbles$.next('');
  }

  showSearchFilterBubbles(data: any) {
    if (this.showFilterBubbles) {
      this.selectedFilterObject = { ...this.selectedFilterObject, searchText: { _id: data, name: data } };
      this.filterBubbles = this.searchFilterBubblesObj();
    }
  }

  ngOnDestroy(): void {
    this.collectionListSearchFilter?.unsubscribe();
    this.collectionSelectionService.updateSelectedItems('collections', this.collectionList, false);
    this.resizeSubscription?.unsubscribe();
    this.collectionSub?.unsubscribe();
    this.collectionListChangeViewSub?.unsubscribe();
    this.collectionListSortBySub?.unsubscribe();
    this.collectionsSearchFilterBubblesSub?.unsubscribe();
  }

  /**
   * Utility method to show/hide loader
   */
  private _showLoader(show = true) {
    this.httpCallInProgress = show;
  }
}
